export interface SpecialtyDistributionItem {
  specialization_id: number | null;
  name_ar: string | null;
  name_en: string | null;
  count: number;
  percentage: number;
}

export interface MonthlyAnsweredTrendItem {
  month: string;
  answered_count: number;
  not_answered_count: number;
}

export interface TopQuestionItem {
  id: number;
  content: string;
  status: string;
  answers_count: number;
  specialization_id: number | null;
  specialization__name_ar: string | null;
  specialization__name_en: string | null;
  first_expert_first_name: string | null;
  first_expert_last_name: string | null;
}

export interface TopExpertItem {
  expert_id: number;
  expert__first_name: string;
  expert__last_name: string;
  answers_count: number;
}

export interface InsightsDashboardData {
  specialty_distribution: SpecialtyDistributionItem[];
  monthly_answered_trend: MonthlyAnsweredTrendItem[];
  top_questions: TopQuestionItem[];
  top_experts: TopExpertItem[];
}

export interface InsightsDashboardStats {
  total_questions_count: number;
  under_review_questions_count: number;
  answered_questions_count: number;
  answered_questions_percentage: number;
  avg_first_response_seconds: number;
}

export interface InsightsDashboardResponse {
  data: InsightsDashboardData;
  error?: string | null;
}

export interface InsightsDashboardStatsResponse {
  data: InsightsDashboardStats;
  error?: string | null;
}

export type InsightsStatusFilter = 'open_not_answered' | 'answered' | 'needs_admin_review' | 'closed' | 'all';

export type InsightsDateRange =
  | 'all_time'
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'
  | 'custom';

export interface InsightsFilters {
  status: InsightsStatusFilter | null;
  specializationId: number | null;
  expertId: number | null;
  // UI-only per design — /dashboard/ has no cohort/group param in the contract, so this is
  // not sent to the API and is ignored by buildInsightsQueryParams until backend adds support.
  cohortId: number | null;
  dateRange: InsightsDateRange;
  dateFrom: string | null;
  dateTo: string | null;
}

export interface InsightsDashboardQueryParams {
  status?: InsightsStatusFilter;
  date_range?: InsightsDateRange;
  date_from?: string;
  date_to?: string;
  specialization?: number;
  expert?: number;
}

export function buildInsightsQueryParams(filters: InsightsFilters): InsightsDashboardQueryParams {
  const params: InsightsDashboardQueryParams = {
    date_range: filters.dateRange,
  };

  if (filters.status) {
    params.status = filters.status;
  }
  if (filters.dateRange === 'custom' && filters.dateFrom && filters.dateTo) {
    params.date_from = filters.dateFrom;
    params.date_to = filters.dateTo;
  }
  if (filters.specializationId) {
    params.specialization = filters.specializationId;
  }
  if (filters.expertId) {
    params.expert = filters.expertId;
  }

  return params;
}

/**
 * monthly_answered_trend is sparse per the API contract — a month is only present if it has
 * ≥1 matching question, gaps are not zero-filled. This fills gaps between the first and last
 * returned month so the chart's x-axis doesn't silently show non-adjacent months as adjacent.
 */
export function fillMonthlyTrendGaps(trend: MonthlyAnsweredTrendItem[]): MonthlyAnsweredTrendItem[] {
  if (trend.length === 0) return trend;

  const toIndex = (month: string): number => {
    const [year, monthNumber] = month.split('-').map(Number);
    return year * 12 + (monthNumber - 1);
  };
  const toMonth = (index: number): string => {
    const year = Math.floor(index / 12);
    const monthNumber = (index % 12) + 1;
    return `${year}-${String(monthNumber).padStart(2, '0')}`;
  };

  const byMonth = new Map(trend.map((item) => [item.month, item]));
  const sortedMonths = trend.map((item) => item.month).sort();
  const startIndex = toIndex(sortedMonths[0]);
  const endIndex = toIndex(sortedMonths[sortedMonths.length - 1]);

  const filled: MonthlyAnsweredTrendItem[] = [];
  for (let index = startIndex; index <= endIndex; index++) {
    const month = toMonth(index);
    filled.push(byMonth.get(month) ?? { month, answered_count: 0, not_answered_count: 0 });
  }
  return filled;
}
